// Openable doors for the map world.
//
// buildings.js cuts the door HOLE and records each opening (centre, wall
// tangent, outward normal, floor/top, wood|metal). This system owns everything
// that makes it a real door instead of a bare gap:
//   • a static, merged FRAME (jambs + header casing) around every opening — a
//     couple of draw calls for the whole map;
//   • a swinging LEAF hinged on one jamb, streamed in only near the player and
//     animated open/closed;
//   • a collider across the opening that is SOLID when shut and clear when
//     open, wired into the same pushOut / vault-probe chain as walls;
//   • F/G interaction to toggle the nearest door.
//
// Materials are StandardMaterial (the map scene has hemi+sun from engine.js;
// a little emissive keeps them readable in shadow) — no facade-shader coupling.
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math';
import { pickNearest } from './door-pick.mjs';

const BASE = 'assets/map10k/';
const CW = 0.16;          // casing width of the frame
const OPEN = 1.92;        // ~110° swing
// Leaves used to stream in at 42 m, so in any shot framed from across a street
// every doorway in the picture was an empty hole — a doorway with no door in it
// is one of the loudest "greybox" tells there is.
const STREAM = 75;        // leaves exist within this radius of the player
const REACH = 2.4;        // interaction distance from the door centre
// nearest() is called once per frame by main.js's interactTarget(); the answer
// cannot change meaningfully faster than this, so it is cached per window.
// label() is NOT cached, so toggling a door re-labels the prompt at once.
const NEAR_MS = 80;       // ~12 Hz
const NEAR_M = 0.2;       // ...unless the player has moved this far

export class Doors {
  constructor(scene, records, terrain, sfx) {
    this.scene = scene;
    this.terrain = terrain;
    this.sfx = sfx;
    this.hash = new Map(); // 16 m cell -> door indices (collision + nearest)
    this._near = null;     // throttled nearest() result (see NEAR_MS)
    this._nearT = -1e9;
    this._nearX = 0; this._nearZ = 0;
    this._nearFX = 0; this._nearFZ = 0;   // the forward the cached answer used

    // materials ---------------------------------------------------------------
    // Indoors these sit outside the interior aperture shader, so a dark tint +
    // a token emissive made every doorway read as a black void rectangle in the
    // beauty shots. Painted casings are LIGHT (soviet enamel) and carry enough
    // ambient lift to stay legible in a room lit only through one window.
    // The map world is lit by self-lit facade/terrain shaders; the scene's own
    // hemi+sun are weak by comparison, so a StandardMaterial casing that looked
    // fine in the gym rendered as a near-black olive slot outdoors. The casings
    // carry most of their value as emissive.
    const frameMat = (name, hex) => {
      const m = new StandardMaterial(name, scene);
      m.diffuseColor = Color3.FromHexString(hex);
      m.emissiveColor = m.diffuseColor.scale(0.62);
      m.specularColor = new Color3(0.05, 0.05, 0.05);
      return m;
    };
    this.frameMatWood = frameMat('doorframe_wood', '#a8967a');
    this.frameMatMetal = frameMat('doorframe_metal', '#a3a7ab');
    const leafMat = (name, tex, tint) => {
      const m = new StandardMaterial(name, scene);
      m.diffuseTexture = new Texture(BASE + tex, scene);
      m.diffuseTexture.uScale = 1.6; m.diffuseTexture.vScale = 2.2;
      m.diffuseColor = tint;
      m.emissiveColor = tint.scale(0.52);
      m.specularColor = new Color3(0.06, 0.06, 0.06);
      m.backFaceCulling = false;
      return m;
    };
    this.leafMatWood = leafMat('doorleaf_wood', 'building-tex/wall_plank.jpg', new Color3(0.78, 0.66, 0.5));
    this.leafMatMetal = leafMat('doorleaf_metal', 'building-tex/wall_metal.jpg', new Color3(0.74, 0.76, 0.78));

    // door objects ------------------------------------------------------------
    this.doors = records.map((r, i) => {
      const h = Math.max(1.6, r.topY - r.floorY);
      const cross = r.tx * r.nz - r.tz * r.nx;        // +Δθ opens outward when <0
      const d = {
        i, cx: r.cx, cz: r.cz, tx: r.tx, tz: r.tz, nx: r.nx, nz: r.nz,
        half: r.half, floorY: r.floorY, height: h, type: r.type,
        baseTheta: Math.atan2(-r.tz, r.tx),           // node yaw so local +X = tangent
        openTarget: (cross > 0 ? -1 : 1) * OPEN,      // swing toward the street
        hingeX: r.cx - r.tx * r.half, hingeZ: r.cz - r.tz * r.half,
        open: false, angle: 0, node: null,
      };
      const key = `${Math.floor(d.cx / 16)}_${Math.floor(d.cz / 16)}`;
      if (!this.hash.has(key)) this.hash.set(key, []);
      this.hash.get(key).push(d);
      return d;
    });

    this._buildFrames();
  }

  // one merged, frozen frame mesh per material (wood / metal) ------------------
  _buildFrames() {
    const bufs = { wood: mkBuf(), metal: mkBuf() };
    for (const d of this.doors) {
      const b = bufs[d.type];
      const midY = d.floorY + d.height / 2 + CW / 2;
      const jHalfV = (d.height + CW) / 2;
      // two vertical jambs, straddling the wall (hn wide both faces)
      for (const s of [-1, 1]) {
        const u = (d.half + CW / 2) * s;
        pushBox(b, d.cx + d.tx * u, midY, d.cz + d.tz * u, d.tx, d.tz, d.nx, d.nz,
          CW / 2, 0.1, jHalfV);
      }
      // header across the top
      pushBox(b, d.cx, d.floorY + d.height + CW / 2, d.cz, d.tx, d.tz, d.nx, d.nz,
        d.half + CW, 0.1, CW / 2);
    }
    this.frameMeshes = [];
    for (const [type, b] of Object.entries(bufs)) {
      if (!b.idx.length) continue;
      const mesh = new Mesh(`doorframes_${type}`, this.scene);
      const vd = new VertexData();
      vd.positions = b.pos; vd.normals = b.norm; vd.uvs = b.uv; vd.indices = b.idx;
      vd.applyToMesh(mesh);
      mesh.material = type === 'wood' ? this.frameMatWood : this.frameMatMetal;
      mesh.isPickable = false;
      mesh.alwaysSelectAsActiveMesh = true;
      mesh.freezeWorldMatrix();
      this.frameMeshes.push(mesh);
    }
  }

  // A plain extruded box is the loudest "programmer art" tell at door range.
  // Leaves are a proper panelled door: two stiles, three rails, two recessed
  // panels and a lever handle — still ONE mesh, built only for streamed-in
  // doors near the player.
  _makeLeaf(d) {
    const node = new TransformNode(`door_${d.i}`, this.scene);
    node.position.set(d.hingeX, d.floorY, d.hingeZ);
    const W = d.half * 2 - 0.05, H = d.height - 0.05, T = 0.062;
    const b = mkBuf();
    const sx = 0.11, ry = 0.13;                 // stile width / rail height
    const box = (cx, cy, cz, hw, hh, hd) => pushBox(b, cx, cy, cz, 1, 0, 0, 0, 1, hw, hd, hh);
    box(sx / 2, H / 2, 0, sx / 2, H / 2, T / 2);
    box(W - sx / 2, H / 2, 0, sx / 2, H / 2, T / 2);
    box(W / 2, ry / 2, 0, W / 2 - sx, ry / 2, T / 2);
    box(W / 2, H * 0.46, 0, W / 2 - sx, ry * 0.8, T / 2);
    box(W / 2, H - ry / 2, 0, W / 2 - sx, ry / 2, T / 2);
    // recessed panels sit 1.5 cm shy of the stile faces
    box(W / 2, H * 0.23 + ry * 0.2, 0, W / 2 - sx, H * 0.23 - ry * 0.7, T / 2 - 0.016);
    box(W / 2, H * 0.73 + ry * 0.1, 0, W / 2 - sx, H * 0.24 - ry * 0.7, T / 2 - 0.016);
    // lever handle on the free edge
    box(W - 0.20, H * 0.44, T / 2 + 0.02, 0.085, 0.022, 0.022);
    const leaf = new Mesh(`leaf_${d.i}`, this.scene);
    const vd = new VertexData();
    vd.positions = b.pos; vd.normals = b.norm; vd.uvs = b.uv; vd.indices = b.idx;
    vd.applyToMesh(leaf);
    leaf.material = d.type === 'wood' ? this.leafMatWood : this.leafMatMetal;
    leaf.isPickable = false;
    leaf.parent = node;
    d.node = node;
    node.rotation.y = d.baseTheta + d.angle;
  }

  // stream leaves near the player + animate swings -----------------------------
  update(playerPos, dt) {
    const R2 = STREAM * STREAM;
    for (const d of this.doors) {
      const dist2 = (d.cx - playerPos.x) ** 2 + (d.cz - playerPos.z) ** 2;
      if (dist2 < R2) {
        if (!d.node) this._makeLeaf(d);
      } else if (d.node && dist2 > R2 * 1.4) {
        d.node.dispose(false, false);
        d.node = null;
      }
      const target = d.open ? d.openTarget : 0;
      if (d.angle !== target) {
        const step = Math.sign(target - d.angle) * Math.min(Math.abs(target - d.angle), dt * 6);
        d.angle += step;
        if (Math.abs(target - d.angle) < 0.001) d.angle = target;
        if (d.node) d.node.rotation.y = d.baseTheta + d.angle;
      }
    }
  }

  // nearest door within reach — feeds the shared F/G interact prompt.
  // `fwd` is the camera forward (x/z); pass it and a door behind you stops
  // being a candidate. See door-pick.mjs for the hash walk and the cone.
  nearest(pos, fwd = null) {
    const t = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // The answer now depends on where the player is LOOKING as well as where
    // they are standing, so the heading has to be part of the cache key: a
    // player who stands still and turns never trips the 0.2 m position test and
    // would get a stale door for a full cache window. FACE_DOT is a ~75° cone,
    // so anything inside ~8° of the cached forward cannot change the verdict.
    const fx = fwd ? fwd.x : 0, fz = fwd ? fwd.z : 0;
    const len = Math.hypot(fx, fz) || 1;
    const turned = Math.abs(fx / len - this._nearFX) > 0.14
      || Math.abs(fz / len - this._nearFZ) > 0.14;
    if (t - this._nearT < NEAR_MS && !turned
      && Math.abs(pos.x - this._nearX) < NEAR_M && Math.abs(pos.z - this._nearZ) < NEAR_M) {
      return this._near;
    }
    this._nearT = t; this._nearX = pos.x; this._nearZ = pos.z;
    this._nearFX = fx / len; this._nearFZ = fz / len;
    this._near = pickNearest(this.hash, pos, fwd, REACH);
    return this._near;
  }

  label(d) { return d.open ? 'Close door' : 'Open door'; }

  // `forced` = shouldered/kicked open rather than worked on the handle: same
  // swing, a great deal more noise.
  toggle(d, forced = false) {
    d.open = !d.open;
    // Opening and closing are different events — a hinge swinging free, then
    // the leaf meeting the frame and the latch dropping in — so they are
    // different roles. The code used to ask for door.wood/door.metal only,
    // and neither was ever curated into sfx-index.json: 839 doors toggled in
    // total silence while still emitting the 20 m noise ping below, punishing
    // the player with a stimulus they never heard. All four roles now exist
    // (tools/curate-sfx.mjs ROLE_BY_ID, takes the game already shipped).
    let role = d.open ? 'door.wood' : 'door.wood_close';
    if (d.type === 'metal') role = d.open ? 'door.metal' : 'door.metal_close';
    // The close takes are measurably quieter recordings than the open ones, so
    // they get a little more gain; `slice` keeps the long creaks to one event
    // instead of the whole recording; a kicked door is louder than a worked
    // handle.
    this.sfx?.play(role, {
      vol: (d.open ? 0.5 : 0.62) * (forced ? 1.5 : 1),
      slice: d.open ? 1.5 : 0.9,
      jitter: 0.05,
    });
    // 20 m of hinge and latch (docs/INFECTED.md Z27/Z118). A steel door bangs
    // louder than a timber one, and one that is forced louder still.
    // `this.noise` is the session NoiseSystem, wired in main.js.
    this.noise?.emit('door', d.cx, d.cz, { mul: forced ? 1.4 : d.type === 'metal' ? 1.15 : 1 });
    return { ok: true, msg: d.open ? 'Opened door' : 'Closed door' };
  }

  // collision — a shut door is solid; an open one lets you through. Mirrors the
  // buildings OBB test but skips doors that are (mostly) open.
  _solid(d) { return Math.abs(d.angle) < 0.4; }

  blockedBand(x, z, qy0, qy1) {
    const kx = Math.floor(x / 16), kz = Math.floor(z / 16);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const list = this.hash.get(`${kx + dx}_${kz + dz}`);
        if (!list) continue;
        for (const d of list) {
          if (!this._solid(d)) continue;
          if (qy0 > d.floorY + d.height || qy1 < d.floorY) continue;
          const px = x - d.cx, pz = z - d.cz;
          const lx = px * d.tx + pz * d.tz;
          const lz = -px * d.tz + pz * d.tx;
          if (Math.abs(lx) < d.half + 0.12 && Math.abs(lz) < 0.2) return true;
        }
      }
    }
    return false;
  }

  pushOut(pos) {
    const kx = Math.floor(pos.x / 16), kz = Math.floor(pos.z / 16);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const list = this.hash.get(`${kx + dx}_${kz + dz}`);
        if (!list) continue;
        for (const d of list) {
          if (!this._solid(d)) continue;
          if (pos.y > d.floorY + d.height - 0.05 || pos.y + 1.7 < d.floorY) continue;
          const hw = d.half, hd = 0.09;
          const px = pos.x - d.cx, pz = pos.z - d.cz;
          const lx = px * d.tx + pz * d.tz;
          const lz = -px * d.tz + pz * d.tx;
          if (Math.abs(lx) >= hw || Math.abs(lz) >= hd) continue;
          const dxp = hw - Math.abs(lx), dzp = hd - Math.abs(lz);
          let ox = 0, oz = 0;
          if (dxp < dzp) ox = lx > 0 ? dxp : -dxp; else oz = lz > 0 ? dzp : -dzp;
          pos.x += ox * d.tx - oz * d.tz;
          pos.z += ox * d.tz + oz * d.tx;
        }
      }
    }
  }
}

// ---- small geometry helpers ------------------------------------------------
function mkBuf() { return { pos: [], norm: [], uv: [], idx: [] }; }

// oriented box: centre (cx,cy,cz), basis T=(tx,0,tz) / N=(nx,0,nz) / up=(0,1,0),
// half-sizes ht (along T), hn (along N), hup (vertical). Flat-shaded, UV=0
// (frames are a painted casing colour, not textured).
function pushBox(b, cx, cy, cz, tx, tz, nx, nz, ht, hn, hup) {
  // UVs run in metres in the box's own basis, offset by the box centre, so the
  // stiles/rails/panels of a door each land on a different bit of the plank
  // texture instead of all sampling texel (0,0).
  const oT = cx * tx + cz * tz, oN = cx * nx + cz * nz;
  const quad = (c0, c1, c2, c3, nrm, ax) => {
    const base = b.pos.length / 3;
    for (const c of [c0, c1, c2, c3]) {
      b.pos.push(cx + c[0] * tx + c[2] * nx, cy + c[1], cz + c[0] * tz + c[2] * nz);
      b.norm.push(nrm[0], nrm[1], nrm[2]);
      if (ax === 0) b.uv.push(oT + c[0], cy + c[1]);
      else if (ax === 1) b.uv.push(oN + c[2], cy + c[1]);
      else b.uv.push(oT + c[0], oN + c[2]);
    }
    b.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  // corners in basis coords [alongT, up, alongN]
  quad([-ht, -hup, hn], [ht, -hup, hn], [ht, hup, hn], [-ht, hup, hn], [nx, 0, nz], 0);
  quad([ht, -hup, -hn], [-ht, -hup, -hn], [-ht, hup, -hn], [ht, hup, -hn], [-nx, 0, -nz], 0);
  quad([ht, -hup, hn], [ht, -hup, -hn], [ht, hup, -hn], [ht, hup, hn], [tx, 0, tz], 1);
  quad([-ht, -hup, -hn], [-ht, -hup, hn], [-ht, hup, hn], [-ht, hup, -hn], [-tx, 0, -tz], 1);
  quad([-ht, hup, hn], [ht, hup, hn], [ht, hup, -hn], [-ht, hup, -hn], [0, 1, 0], 2);
  quad([-ht, -hup, -hn], [ht, -hup, -hn], [ht, -hup, hn], [-ht, -hup, hn], [0, -1, 0], 2);
}
